import type { MarketDataPreferenceRequest, ResolveInstrumentResponse } from "../types/instruments";
import type { MarketDataSourceOption } from "../types/marketDataSources";
import {
  inferBinanceProductLane,
  marketDataBrokerKey,
} from "./marketDataSourceChoice";
import { coinapiSymbolIdFromListing } from "./marketDataAttributionCopy";

export type MarketDataPreferenceSelection = {
  assetClass: string;
  marketDataBroker?: string;
  marketDataSource?: MarketDataSourceOption;
};

/** Optional listing fields BE may attach to a market_data_sources entry. */
type MarketDataSourcePreferenceFields = {
  md_listing_key?: string;
  binance_pair?: string;
  binance_product?: "spot" | "futures";
};

/** Binance product must be spot|futures; never fall back to IBKR/OANDA venue codes. */
export function normalizeBinanceProduct(
  value: string | null | undefined
): "spot" | "futures" | undefined {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (normalized === "spot" || normalized === "futures") return normalized;
  if (
    normalized.includes(":futures:") ||
    normalized.includes("futures") ||
    normalized.includes("usd-m") ||
    normalized.includes("usdⓈ-m") ||
    normalized.includes("perpetual")
  ) {
    return "futures";
  }
  if (normalized.includes(":spot:") || normalized.includes("spot")) {
    return "spot";
  }
  return undefined;
}

function alignMdListingKeyWithBinanceProduct(
  mdListingKey: string | undefined,
  product: "spot" | "futures" | undefined
): string | undefined {
  if (!mdListingKey?.trim() || !product) return mdListingKey;
  if (product === "futures" && mdListingKey.includes(":spot:")) {
    return mdListingKey.replace(":spot:", ":futures:");
  }
  if (product === "spot" && mdListingKey.includes(":futures:")) {
    return mdListingKey.replace(":futures:", ":spot:");
  }
  return mdListingKey;
}

function readMarketDataSourcePreferenceFields(
  source?: MarketDataSourceOption
): MarketDataSourcePreferenceFields {
  if (!source) return {};
  const record = source as MarketDataSourceOption & {
    binance_pair?: string;
    binance_product?: string;
  };
  const fields: MarketDataSourcePreferenceFields = {
    md_listing_key: record.md_listing_key,
  };

  // Only Binance listings carry binance_* fields. Never map venue (e.g. NYBOT) into
  // binance_product — that produced 400 "Choose spot or futures for Binance."
  if (marketDataBrokerKey(source) === "binance") {
    fields.binance_pair = record.binance_pair;
    fields.binance_product =
      normalizeBinanceProduct(record.binance_product) ||
      inferBinanceProductLane(source) ||
      normalizeBinanceProduct(record.venue);
  }

  return fields;
}

export function buildMarketDataPreferenceRequest(
  brokerAccountCode: string,
  symbol: string,
  selection: MarketDataPreferenceSelection,
  resolveContext?: Pick<ResolveInstrumentResponse, "md_listing_key" | "instrument_id"> | null,
  options?: { resolvedSymbol?: string; oandaInstrument?: string }
): MarketDataPreferenceRequest {
  // Bind preference from the chosen market_data_sources[] row — never invent listing keys.
  const source = selection.marketDataSource;
  const preferenceFields = readMarketDataSourcePreferenceFields(source);
  const marketDataBroker =
    (source?.market_data_broker ?? source?.broker ?? selection.marketDataBroker)?.trim() ||
    undefined;

  // Prefer the selected source listing; never reuse a stale resolveContext key when the
  // caller is persisting a new quote variant (resolved_symbol).
  let mdListingKey =
    preferenceFields.md_listing_key ??
    (preferenceFields.binance_product || options?.resolvedSymbol?.trim()
      ? undefined
      : resolveContext?.md_listing_key);
  mdListingKey = alignMdListingKeyWithBinanceProduct(
    mdListingKey,
    preferenceFields.binance_product
  );

  const request: MarketDataPreferenceRequest = {
    broker_account_code: brokerAccountCode,
    symbol,
    consent: true,
    asset_class: selection.assetClass.trim().toLowerCase(),
    market_data_broker: marketDataBroker,
    md_listing_key: mdListingKey,
    instrument_id: resolveContext?.instrument_id,
    ibkr_conid: source?.ibkr_conid ?? undefined,
    ibkr_exchange: source?.ibkr_exchange ?? undefined,
    resolved_symbol: options?.resolvedSymbol?.trim() || undefined,
    oanda_instrument: options?.oandaInstrument?.trim() || undefined,
  };

  if (preferenceFields.binance_pair) {
    request.binance_pair = preferenceFields.binance_pair;
  }
  if (preferenceFields.binance_product) {
    request.binance_product = preferenceFields.binance_product;
  }

  if (source?.option_id?.trim()) {
    request.option_id = source.option_id.trim();
  }

  if (marketDataBroker === "coinapi") {
    const coinapiSymbolId = coinapiSymbolIdFromListing(
      source?.option_id,
      mdListingKey ?? source?.md_listing_key
    );
    if (coinapiSymbolId) {
      request.coinapi_symbol_id = coinapiSymbolId;
    }
  }

  return request;
}
